import { useState } from 'react';
import { velocityApi } from '../api/velocity';
import { usePoll } from '../hooks/usePoll';
import { errorMessage, useApp } from '../app-context';
import { Dialog, ErrorAlert } from '../components/ui';
import { Spinner } from '../components/feedback';
const major = (value?: string) => /^(d+)./.exec(value ?? '')?.[1];

export function VelocityVersion({ current, reload }: { current?: string; reload: () => void }) {
  const { canOperate, notify } = useApp();
  const [version, setVersion] = useState('');
  const [build, setBuild] = useState<number>();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const catalog = usePoll(() => velocityApi.versions(version || undefined), 60000, [version]);
  const ready = !catalog.loading && catalog.data?.version === (version || catalog.data?.latest);
  const target = version || catalog.data?.latest;
  const selected = ready ? catalog.data?.builds.find((item) => item.id === build) : undefined;
  async function apply() {
    if (!selected || !catalog.data) return;
    setBusy(true);
    try {
      await velocityApi.version(version || catalog.data.latest, selected.id);
      setConfirm(false);
      notify('Velocity の本体を更新しました');
      reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card card-body stack">
      <h2>Velocity のバージョン</h2>
      <div className="row">
        <span>現在: {current ?? '不明'}</span>
        <span>最新の正式版: {catalog.data?.latest ?? '取得中…'}</span>
      </div>
      <ErrorAlert error={catalog.error} />
      <label>
        版
        <select
          className="input"
          value={version || catalog.data?.latest || ''}
          disabled={busy}
          onChange={(event) => {
            setVersion(event.target.value);
            setBuild(undefined);
          }}
        >
          {catalog.data?.versions.map((value) => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      <label>
        ビルド
        <select
          className="input"
          value={build ?? ''}
          disabled={busy || !ready}
          onChange={(event) => setBuild(Number(event.target.value))}
        >
          <option value="">ビルドを選択</option>
          {catalog.data?.builds.map((item) => (
            <option key={item.id} value={item.id}>
              {item.id}（
              {(
                { STABLE: '安定版', RECOMMENDED: '推奨版', BETA: 'ベータ版', ALPHA: '開発版' } as Record<string, string>
              )[item.channel] ?? 'その他'}
              ）
            </option>
          ))}
        </select>
      </label>
      {current && target && major(current) !== major(target) && (
        <p className="alert warning">
          メジャー版が変わります（{major(current)} 系 → {major(target)} 系）。Velocity のプラグイン（Geyser
          など）が新しい版に対応しているか確かめてから適用してください。
        </p>
      )}
      <p className="small muted">更新すると Velocity を再起動します。起動に失敗した場合は前の jar に戻します。</p>
      {canOperate && (
        <button className="btn primary" disabled={!selected || busy || !ready} onClick={() => setConfirm(true)}>
          {busy && <Spinner />}適用
        </button>
      )}
      {confirm && (
        <Dialog
          title="Velocity の本体を更新"
          onClose={() => setConfirm(false)}
          actions={
            <button className="btn danger" disabled={busy} onClick={() => void apply()}>
              {busy && <Spinner />}更新して再起動
            </button>
          }
        >
          <p>
            {version || catalog.data?.latest} ビルド {build} を適用します。接続中の全プレイヤーが切断されます。
          </p>
        </Dialog>
      )}
    </section>
  );
}
